import type { AiContext } from '@/components/chat/types/ai-context';
import type { SuggestedAction } from '@/data/chat/chat';

import { getReviewDecisionFilenames } from '@/lib/pr-v2/file-review-decisions';

export type FollowUpSuggestion = SuggestedAction & {
  kind: NonNullable<SuggestedAction['kind']>;
  source: 'deterministic' | 'llm';
  /** Set on navigation pills; used for click-time staleness revalidation. */
  targetFilename?: string;
};

type BuildDeterministicFollowUpsArgs = {
  aiContext: AiContext;
  viewedFiles: string[];
};

/**
 * Computes the navigation follow-up pill ("Show me the next risky file")
 * without an LLM: review-plan order minus the files the user already marked
 * viewed. Free, instant, and always consistent with the diff's viewed state —
 * unlike a generated suggestion, it can't go stale when the user keeps
 * reviewing between turns.
 */
export function buildDeterministicFollowUps({
  aiContext,
  viewedFiles,
}: BuildDeterministicFollowUpsArgs): FollowUpSuggestion[] {
  const viewed = new Set(viewedFiles);

  const orderedFilenames =
    aiContext.orderedFiles && aiContext.orderedFiles.length > 0
      ? [...aiContext.orderedFiles]
          .sort((a, b) =>
            a.groupOrder !== b.groupOrder
              ? a.groupOrder - b.groupOrder
              : a.orderNumber - b.orderNumber,
          )
          .map((file) => file.filename)
      : (aiContext.files ?? []).map((file) => file.filename);

  const remaining = orderedFilenames.filter((filename) => !viewed.has(filename));
  if (remaining.length === 0) {
    return [];
  }

  const reviewDecisionFilenames = getReviewDecisionFilenames(aiContext.reviewDecisions);
  const decisionTarget = remaining.find((filename) => reviewDecisionFilenames.has(filename));

  const targetFilename = decisionTarget ?? remaining[0];
  if (!targetFilename) {
    return [];
  }

  return [
    {
      kind: 'navigate',
      source: 'deterministic',
      targetFilename,
      title: decisionTarget ? 'Show me the next decision' : 'Show me the next file to review',
      action: `Show me what changed in ${targetFilename} and explain what to check there.`,
    },
  ];
}
